import type { ReactElement } from "react";

import styles from "./Switch.module.css";

export interface SwitchProps {
  /** The visible text, which is also the accessible name (it comes from `t()`). */
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean | undefined;
  readonly id?: string | undefined;
  readonly describedBy?: string | undefined;
}

/**
 * An on/off setting that takes effect at once (the mockup's `.switch` inside `.sw-row`).
 *
 * A real `<button role="switch">`: Tab reaches it, Space and Enter press it, and `aria-checked`
 * carries the state. The track and the text live inside the one button, so the whole row is the
 * hit target and the text is the name, with no `<label>` wiring to get wrong.
 */
export function Switch({ label, checked, onChange, disabled = false, id, describedBy }: SwitchProps): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      className={styles.row}
      aria-checked={checked}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className={styles.track} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
